import type { SEmbedPosthogConfig } from "./analyticsProtocol";

export type SEmbedReadyMessage = { type: "S_EMBED_READY" };
export type SEmbedNavigationChangeMessage = {
  type: "S_EMBED_NAVIGATION_CHANGE";
  path: string;
};
export type SEmbedLinkMessage = { type: "S_EMBED_LINK"; url: string };
export type SEmbedPickerSelectMessage = {
  type: "S_EMBED_PICKER_SELECT";
  key: string;
  optionId: string;
};
export type SEmbedRefreshMessage = { type: "S_EMBED_REFRESH" };
export type SEmbedTheme = "light" | "dark" | "system";
/**
 * The user picked a theme in S's profile menu. S applies it at once and asks the host to save it;
 * the host answers with `S_EMBED_THEME`, whose value wins.
 */
export type SEmbedThemeChangeMessage = { type: "S_EMBED_THEME_CHANGE"; theme: SEmbedTheme };
/** The host's theme, which S applies at once. The host also loads S with it as `?theme`. */
export type SEmbedThemeMessage = { type: "S_EMBED_THEME"; theme: SEmbedTheme };

export function isSEmbedTheme(value: unknown): value is SEmbedTheme {
  return value === "light" || value === "dark" || value === "system";
}
export type SEmbedTokenMessage = {
  type: "S_EMBED_TOKEN";
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  dataContext?: string;
  // `theme: "host"` tells S the host owns the theme, so S shows its Theme entry.
  host?: { navigation?: "host"; overlays?: "host"; theme?: "host" };
  posthog?: SEmbedPosthogConfig;
};

export type SEmbedHostMessage =
  | SEmbedReadyMessage
  | SEmbedNavigationChangeMessage
  | SEmbedLinkMessage
  | SEmbedPickerSelectMessage
  | SEmbedRefreshMessage
  | SEmbedThemeChangeMessage;

export function isSEmbedHostMessage(value: unknown): value is SEmbedHostMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Record<string, unknown>;
  switch (message.type) {
    case "S_EMBED_READY":
    case "S_EMBED_REFRESH":
      return true;
    case "S_EMBED_NAVIGATION_CHANGE":
      return typeof message.path === "string";
    case "S_EMBED_LINK":
      return typeof message.url === "string";
    case "S_EMBED_PICKER_SELECT":
      return typeof message.key === "string" && typeof message.optionId === "string";
    case "S_EMBED_THEME_CHANGE":
      return isSEmbedTheme(message.theme);
    default:
      return false;
  }
}
